import React, { useState, useEffect } from 'react';
import s from './LoginScreen.module.sass';

const LoginScreen = ({ onLogin }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    // Тема входа хранится в том же ключе, что и Nova ('nova-mode'), поэтому
    // выбор здесь сохраняется и применяется дальше во всём приложении
    const [mode, setMode] = useState(() => {
        try { return localStorage.getItem('nova-mode') === 'light' ? 'light' : 'dark'; }
        catch (_) { return 'dark'; }
    });
    const toggleTheme = () => {
        setMode((prev) => {
            const next = prev === 'dark' ? 'light' : 'dark';
            try { localStorage.setItem('nova-mode', next); } catch (_) { /* приватный режим */ }
            return next;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!username.trim() || !password.trim()) {
            setError('Введите логин и пароль');
            return;
        }

        try {
            setLoading(true);
            setError('');

            const response = await fetch('/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: username.trim(),
                    password: password.trim()
                })
            });

            const data = await response.json();
            if (response.ok && data?.success && data?.token && data?.user) {
                localStorage.setItem('isAuthenticated', 'true');
                localStorage.setItem('authToken', data.token);
                localStorage.setItem('currentUser', JSON.stringify({
                    username: data.user.username,
                    id: data.user.id,
                    role: data.user.role
                }));
                onLogin(data.user);
            } else {
                setError(data?.error || 'Неверный логин или пароль');
            }
        } catch (err) {
            console.error('Ошибка входа:', err);
            setError('Ошибка при входе. Попробуйте еще раз.');
        } finally {
            setLoading(false);
        }
    };

    // Проверяем, не авторизован ли пользователь уже
    useEffect(() => {
        const isAuthenticated = localStorage.getItem('isAuthenticated');
        if (isAuthenticated === 'true') {
            const currentUser = localStorage.getItem('currentUser');
            if (currentUser) {
                try {
                    const user = JSON.parse(currentUser);
                    onLogin(user);
                } catch (e) {
                    localStorage.removeItem('isAuthenticated');
                    localStorage.removeItem('currentUser');
                    localStorage.removeItem('authToken');
                }
            }
        }
    }, [onLogin]);

    return (
        <div className={s.loginScreen} data-nova-mode={mode}>
            <div className={s.aurora} aria-hidden="true" />
            <div className={s.grid} aria-hidden="true" />

            <div className={s.loginContainer}>
                <form onSubmit={handleSubmit} className={s.loginForm}>
                    {error && (
                        <div className={s.errorMessage}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                            <span>{error}</span>
                        </div>
                    )}

                    <div className={s.field}>
                        <input
                            type="text"
                            id="login-username"
                            className={s.input}
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            placeholder=" "
                            autoFocus
                            disabled={loading}
                        />
                        <label htmlFor="login-username" className={s.label}>Логин</label>
                        <fieldset className={s.notch} aria-hidden="true">
                            <legend className={s.legend}><span>Логин</span></legend>
                        </fieldset>
                    </div>

                    <div className={s.field}>
                        <input
                            type={showPassword ? 'text' : 'password'}
                            id="login-password"
                            className={`${s.input} ${s.inputPassword}`}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder=" "
                            disabled={loading}
                        />
                        <label htmlFor="login-password" className={s.label}>Пароль</label>
                        <fieldset className={s.notch} aria-hidden="true">
                            <legend className={s.legend}><span>Пароль</span></legend>
                        </fieldset>
                        <button
                            type="button"
                            className={s.togglePassword}
                            onClick={() => setShowPassword(!showPassword)}
                            disabled={loading}
                            aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
                        >
                            {showPassword ? (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                    <line x1="1" y1="1" x2="23" y2="23" />
                                </svg>
                            ) : (
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                    <circle cx="12" cy="12" r="3" />
                                </svg>
                            )}
                        </button>
                    </div>

                    <button
                        type="submit"
                        className={s.submitButton}
                        disabled={loading}
                    >
                        {loading ? (
                            <span className={s.spinner} aria-hidden="true" />
                        ) : (
                            <>
                                Войти
                                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                    <polyline points="12 5 19 12 12 19" />
                                </svg>
                            </>
                        )}
                    </button>
                </form>
            </div>

            {/* Переключатель темы — тот же, что на лендинге: пилюля с ползунком,
                солнце/луна кроссфейдом. Под формой по центру */}
            <button
                type="button"
                className={s.themeToggle}
                onClick={toggleTheme}
                aria-label="Сменить тему"
                title="Сменить тему"
            >
                <span className={s.themeKnob}>
                    <svg className={s.themeSun} viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
                    <svg className={s.themeMoon} viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.8A8.5 8.5 0 1 1 11.2 3a6.5 6.5 0 0 0 9.8 9.8Z" /></svg>
                </span>
            </button>
        </div>
    );
};

export default LoginScreen;

